import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trash2, Download, Image, Music, Search, Filter, Folder, ChevronRight, ChevronDown, Plus, FolderOpen, Edit2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { mediaService } from '../../lib/services/mediaService';
import { useNews } from '../../hooks/useNews';
import { useNewsImages } from '../../hooks/useNewsImages';
import { toast } from 'react-hot-toast';
import type { NewsArticleWithRelations } from '../../types/news';
import { useInView } from 'react-intersection-observer';
import { supabase } from '@/lib/supabase';

interface MediaItem {
  id: string;
  name: string;
  type: 'image' | 'audio';
  url: string;
  source: 'wordpress' | 'supabase';
  createdAt: string;
  size: number;
  articleId?: string;
}

interface FolderStructure {
  id: string;
  name: string;
  type: 'folder';
  items: MediaItem[];
  isOpen: boolean;
  currentPage: number;
  hasMore: boolean;
}

interface RootFolder extends Omit<FolderStructure, 'items'> {
  items: (FolderStructure | MediaItem)[];
  currentPage: number;
  hasMore: boolean;
}

const ITEMS_PER_PAGE = 12;
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiMyRDM3NDgiLz48cGF0aCBkPSJNODIgODhINzBWMTAwSDgyVjg4Wk0xMTggODhIMTA2VjEwMEgxMThWODhaTTgyIDEwNkg3MFYxMThIODJWMTA2Wk0xMTggMTA2SDEwNlYxMThIMTE4VjEwNloiIGZpbGw9IiM0QjU1NjMiLz48L3N2Zz4=';

const UploadCard = ({ 
  folderId, 
  folderName, 
  parentId, 
  onUploadComplete 
}: { 
  folderId: string; 
  folderName: string; 
  parentId?: string;
  onUploadComplete: () => Promise<void>;
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles) => {
      setIsUploading(true);
      try {
        for (const file of acceptedFiles) {
          await mediaService.uploadToSupabase(file, folderId);
          toast.success(`${file.name} carregado com sucesso para ${folderName}`);
        }
        await onUploadComplete();
      } catch (error) {
        toast.error('Erro ao carregar ficheiros');
        console.error(error);
      } finally {
        setIsUploading(false);
      }
    },
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'audio/*': ['.mp3', '.wav']
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`relative aspect-square bg-gray-800 rounded-lg overflow-hidden border-2 border-dashed transition-colors
        ${isDragActive ? 'border-blue-500 bg-gray-700' : 'border-gray-600 hover:border-blue-500'}
        ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
        {isUploading ? (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
            <p className="text-sm text-gray-300 text-center">A carregar...</p>
          </>
        ) : (
          <>
            <Plus size={32} className="text-gray-400 mb-2" />
            <p className="text-sm text-gray-300 text-center">
              Adicionar ficheiros
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {parentId ? folderName : `Pasta: ${folderName}`}
            </p>
          </>
        )}
      </div>
    </motion.div>
  );
};

const MediaManager = () => {
  const [rootFolders, setRootFolders] = useState<RootFolder[]>([
    { id: 'news', name: 'Notícias', type: 'folder', items: [], isOpen: false, currentPage: 1, hasMore: true },
    { id: 'podcasts', name: 'Podcasts', type: 'folder', items: [], isOpen: false, currentPage: 1, hasMore: true },
    { id: 'team', name: 'Equipa', type: 'folder', items: [], isOpen: false, currentPage: 1, hasMore: true },
    { id: 'courses', name: 'Cursos', type: 'folder', items: [], isOpen: false, currentPage: 1, hasMore: true },
    { id: 'general', name: 'Geral', type: 'folder', items: [], isOpen: false, currentPage: 1, hasMore: true }
  ]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<{ id: string; name: string; parentId?: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'images' | 'audio'>('all');
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [editingFileName, setEditingFileName] = useState('');
  const { fetchArticles } = useNews();
  const { fetchImagesByArticleId } = useNewsImages();
  const [loadingFolders, setLoadingFolders] = useState<{[key: string]: boolean}>({});
  const [allFoldersOpen, setAllFoldersOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadMediaItems();
  }, []);

  const loadMediaItems = async () => {
    console.log('Iniciando carregamento de itens de média...');
    setIsLoading(true);
    setError(null);

    try {
      const generalItems = await mediaService.listMediaFiles('general');
      console.log('General folder items:', generalItems);

      // Load team images
      const teamItems = await mediaService.listMediaFiles('team');
      console.log('Team folder items:', teamItems);
      
      // Load courses images
      const coursesItems = await mediaService.listMediaFiles('courses');
      console.log('Courses folder items:', coursesItems);

      const articles = await fetchArticles();
      console.log('Notícias carregadas:', articles);

      const newsFolders: FolderStructure[] = await Promise.all(
        articles.map(async (article) => {
          const images = await fetchImagesByArticleId(article.id);
          console.log(`Imagens para o artigo ${article.title}:`, images);

          const mediaItems: MediaItem[] = images.map(img => {
            let imageUrl = img.url;
            if (!imageUrl.startsWith('http')) {
              const { data: { publicUrl } } = supabase.storage
                .from('news')
                .getPublicUrl(img.url);
              imageUrl = publicUrl;
            }

            return {
              id: img.id,
              name: img.alt || 'Sem nome',
              type: 'image',
              url: imageUrl,
              source: 'supabase',
              createdAt: img.created_at,
              size: 0,
              articleId: article.id
            };
          });

          return {
            id: article.id,
            name: article.title,
            type: 'folder',
            items: mediaItems,
            isOpen: false,
            currentPage: 1,
            hasMore: mediaItems.length >= ITEMS_PER_PAGE
          };
        })
      );

      setRootFolders(prevFolders => 
        prevFolders.map(folder => 
          folder.id === 'general' 
            ? { ...folder, items: generalItems }
            : folder.id === 'team'
            ? { ...folder, items: teamItems }
            : folder.id === 'courses'
            ? { ...folder, items: coursesItems }
            : folder.id === 'news'
            ? { 
                ...folder, 
                items: newsFolders.map(newFolder => {
                const existingFolder = folder.items.find(item => 'items' in item && item.id === newFolder.id);
                return existingFolder ? { ...newFolder, isOpen: existingFolder.isOpen } : newFolder;
              }) }
            : folder
        )
      );

      console.log('Estrutura de pastas atualizada');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      console.error('Erro ao carregar ficheiros multimédia:', err);
      setError(errorMessage);
      toast.error(`Erro ao carregar ficheiros multimédia: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFolder = (folderId: string) => {
    setRootFolders(prevFolders => {
      const updateFolderRecursively = (items: (FolderStructure | MediaItem)[]): (FolderStructure | MediaItem)[] => {
        return items.map(item => {
          if ('items' in item) {
            if (item.id === folderId) {
              return { ...item, isOpen: !item.isOpen };
            }
            return { ...item, items: updateFolderRecursively(item.items) };
          }
          return item;
        });
      };

      return prevFolders.map(folder => {
        if (folder.id === folderId) {
          return { ...folder, isOpen: !folder.isOpen };
        }
        return { ...folder, items: updateFolderRecursively(folder.items) };
      });
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!selectedFolder) {
      toast.error('Por favor, selecione uma pasta para fazer upload');
      return;
    }
    const toastId = 'upload-toast';
    toast.loading('A carregar ficheiros...', { id: toastId });

    setIsUploading(true);
    try {
      for (const file of acceptedFiles) {
        await mediaService.uploadToSupabase(file, selectedFolder.id);
      }
      toast.success('Ficheiros carregados com sucesso!', { id: toastId });
      await loadMediaItems();
    } catch (error) {
      toast.error('Erro ao carregar ficheiros', { id: toastId });
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  }, [selectedFolder]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'audio/*': ['.mp3', '.wav']
    }
  });

  const handleDelete = async (ids: string[]) => {
    try {
      toast.loading('A eliminar ficheiros...', { id: 'delete-toast' });

      const currentFolderStates = rootFolders.map(folder => ({
        id: folder.id,
        isOpen: folder.isOpen,
        items: folder.items.map(item => {
          if ('items' in item) {
            return { id: item.id, isOpen: item.isOpen };
          }
          return null;
        }).filter(Boolean)
      }));

      for (const id of ids) {
        // Find the folder containing this file to determine the bucket
        let bucket = 'general';
        let isInNewsSubfolder = false;
        
        for (const folder of rootFolders) {
          // First check if the file is in the main folder
          const isInMainFolder = folder.items.some(item => !('items' in item) && item.id === id);
          if (isInMainFolder) {
            bucket = folder.id;
            break;
          }

          // Then check subfolders
          const subFolder = folder.items.find(item => 
            'items' in item && item.items.some(subItem => subItem.id === id)
          );

          if (subFolder) {
            if (folder.id === 'news') {
              bucket = 'news';
              isInNewsSubfolder = true;
            } else {
              bucket = folder.id;
            }
            break;
          }
        }

        // If the file is in a news subfolder, we need to handle it differently
        if (isInNewsSubfolder) {
          // First delete from database
          const { error: dbError } = await supabase
            .from('news_images')
            .delete()
            .eq('id', id);

          if (dbError) {
            console.error('Error deleting from database:', dbError);
            throw dbError;
          }
        }

        // Then delete from storage
        await mediaService.deleteFromSupabase(id, bucket);
      }
      toast.success('Ficheiros eliminados com sucesso! 🗑️', { id: 'delete-toast' });

      await loadMediaItems();
      setRootFolders(prevFolders => {
        return prevFolders.map(folder => {
          const savedState = currentFolderStates.find(state => state.id === folder.id);
          if (savedState) {
            return {
              ...folder,
              isOpen: savedState.isOpen,
              items: folder.items.map(item => {
                if ('items' in item) {
                  const savedItemState = savedState.items.find(si => si.id === item.id);
                  return savedItemState ? { ...item, isOpen: savedItemState.isOpen } : item;
                }
                return item;
              })
            };
          }
          return folder;
        });
      });

      setSelectedItems([]);
    } catch (error) {
      toast.error('Erro ao eliminar ficheiros ❌', { id: 'delete-toast' });
      console.error(error);
    }
  };

  const handleDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleFolderSelect = (folderId: string, folderName: string, parentId?: string) => {
    setSelectedFolder({ id: folderId, name: folderName, parentId });
  };

  const loadMoreItems = useCallback(async (folderId: string, subFolderId?: string) => {
    const loadingKey = subFolderId ? `${folderId}-${subFolderId}` : folderId;
    if (loadingFolders[loadingKey]) return;

    setLoadingFolders(prev => ({ ...prev, [loadingKey]: true }));
    try {
      setRootFolders(prevFolders => {
        return prevFolders.map(folder => {
          if (folder.id === folderId) {
            if (subFolderId) {
              const updatedItems = folder.items.map(item => {
                if ('items' in item && item.id === subFolderId) {
                  return {
                    ...item,
                    currentPage: item.currentPage + 1,
                    hasMore: item.items.length >= ITEMS_PER_PAGE
                  };
                }
                return item;
              });
              return { ...folder, items: updatedItems };
            } else {
              return {
                ...folder,
                currentPage: folder.currentPage + 1,
                hasMore: folder.items.length >= ITEMS_PER_PAGE
              };
            }
          }
          return folder;
        });
      });
    } catch (error) {
      console.error('Erro ao carregar mais itens:', error);
      toast.error('Erro ao carregar mais itens');
    } finally {
      setLoadingFolders(prev => ({ ...prev, [loadingKey]: false }));
    }
  }, [loadingFolders]);

  const handleRenameFile = async (file: MediaItem, newName: string) => {
    const toastId = 'rename-toast';
    toast.loading('A renomear ficheiro...', { id: toastId });

    try {
      await mediaService.renameFile(file.id, newName);
      setEditingFileId(null);
      setEditingFileName('');
      toast.success('Nome do ficheiro atualizado com sucesso', { id: toastId });
      await loadMediaItems();
    } catch (error) {
      console.error('Error renaming file:', error);
      toast.error('Erro ao renomear ficheiro', { id: toastId });
    }
  };

  const LoadMoreTrigger = ({ folderId, subFolderId }: { folderId: string, subFolderId?: string }) => {
    const [ref, inView] = useInView({
      threshold: 0.1,
      rootMargin: '100px',
      delay: 100
    });

    const loadingKey = subFolderId ? `${folderId}-${subFolderId}` : folderId;
    const isLoading = loadingFolders[loadingKey];

    useEffect(() => {
      if (inView && !isLoading) {
        loadMoreItems(folderId, subFolderId);
      }
    }, [inView, folderId, subFolderId, isLoading]);

    return (
      <div ref={ref} className="col-span-full flex justify-center items-center py-4">
        {isLoading && (
          <div className="flex items-center gap-2 text-gray-400">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            <span className="text-sm">A carregar mais itens...</span>
          </div>
        )}
      </div>
    );
  };

  const filteredFolders = rootFolders
    .filter(folder => 
      folder.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      folder.items.some(item => {
        if ('name' in item) {
          return item.name.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return false;
      })
    )
    .map(folder => ({
      ...folder,
      items: folder.items.filter(item => {
        if (!('type' in item)) return true;
        if (filter === 'all') return true;
        return filter === item.type + 's';
      })
    }));

  if (error) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-lg">
        <div className="text-red-400 mb-4">
          <h2 className="text-lg font-semibold">Erro ao carregar média</h2>
          <p>{error}</p>
        </div>
        <button
          onClick={loadMediaItems}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-lg text-gray-100">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-100 mb-2">Gestor de Média</h1>
        <p className="text-gray-300">Gerir imagens e áudio</p>
        <p className="text-sm text-gray-400 mt-1">
          {rootFolders.length} {rootFolders.length === 1 ? 'pasta' : 'pastas'} com média
        </p>
      </div>

      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex gap-2 flex-1 min-w-[300px]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Pesquisar ficheiros ou notícias..."
              className="w-full pl-10 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-100 placeholder-gray-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <button
            onClick={() => {
              setAllFoldersOpen(!allFoldersOpen);
              setRootFolders(prevFolders => {
                return prevFolders.map(folder => ({
                  ...folder,
                  isOpen: !allFoldersOpen,
                  items: folder.items.map(item => {
                    if ('items' in item) {
                      return { ...item, isOpen: !allFoldersOpen };
                    }
                    return item;
                  })
                }));
              });
            }}
            className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-all duration-200 text-gray-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {allFoldersOpen ? (
              <FolderOpen size={20} className="transition-transform duration-200" />
            ) : (
              <Folder size={20} className="transition-transform duration-200" />
            )}
          </button>
        </div>

        <div className="flex gap-2">
          <select
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-100"
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
          >
            <option value="all">Todos os ficheiros</option>
            <option value="images">Imagens</option>
            <option value="audio">Áudio</option>
          </select>

          {selectedItems.length > 0 && (
            <button
              onClick={() => handleDelete(selectedItems)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              <Trash2 size={20} />
              Eliminar ({selectedItems.length})
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {filteredFolders.map((rootFolder) => (
          <div key={rootFolder.id} className="border-2 border-gray-600 rounded-lg overflow-hidden bg-gray-900">
            <div
              className={`flex items-center p-5 bg-gray-800 cursor-pointer hover:bg-gray-700 transition-colors border-b-2 border-gray-600
                ${selectedFolder?.id === rootFolder.id ? 'bg-gray-700 ring-2 ring-blue-500' : ''}`}
              onClick={() => {
                toggleFolder(rootFolder.id);
                handleFolderSelect(rootFolder.id, rootFolder.name);
              }}
            >
              {rootFolder.isOpen ? <ChevronDown size={24} className="text-gray-300" /> : <ChevronRight size={24} className="text-gray-300" />}
              <Folder className="ml-3 text-blue-400" size={24} />
              <span className="ml-3 font-bold text-xl text-gray-100">{rootFolder.name}</span>
              <span className="ml-3 text-sm text-gray-400">
                ({rootFolder.items.length} {rootFolder.items.length === 1 ? 'item' : 'items'})
              </span>
            </div>

            {rootFolder.isOpen && (
              <div className="p-4 bg-gray-900">
                {(rootFolder.id === 'general' || rootFolder.id === 'courses' || rootFolder.id === 'team') && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    <UploadCard 
                      folderId={rootFolder.id} 
                      folderName={rootFolder.name} 
                      onUploadComplete={loadMediaItems}
                    />
                    {rootFolder.items.map((item) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`relative group bg-gray-800 rounded-lg overflow-hidden border border-gray-700 hover:border-gray-600
                          ${selectedItems.includes(item.id) ? 'ring-2 ring-blue-500' : ''}`}
                        onClick={() => {
                          setSelectedItems(prev =>
                            prev.includes(item.id)
                              ? prev.filter(i => i !== item.id)
                              : [...prev, item.id]
                          );
                        }}
                      >
                        <div className="aspect-square relative">
                          {item.type === 'image' ? (
                            <div className="w-full h-full">
                              <img
                                src={item.url}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = PLACEHOLDER_IMAGE;
                                }}
                              />
                              {/* Added delete button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete([item.id]);
                                }}
                                className="absolute top-2 right-2 p-1.5 bg-destructive/80 hover:bg-destructive text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-800">
                              <Music size={48} className="text-gray-500" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          {editingFileId === item.id ? (
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="text"
                                value={editingFileName}
                                onChange={(e) => setEditingFileName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleRenameFile(item, editingFileName);
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    setEditingFileId(null);
                                    setEditingFileName('');
                                  }
                                }}
                                className="w-full px-2 py-1 bg-gray-700 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none text-gray-200"
                                autoFocus
                              />
                            </div>
                          ) : (
                            <p className="font-medium text-gray-200 truncate">{item.name}</p>
                          )}
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-400">
                              {new Date(item.createdAt || new Date()).toLocaleDateString()}
                            </p>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingFileId(item.id);
                                  setEditingFileName(item.name);
                                }}
                                className="p-1 bg-gray-700 rounded-full shadow-lg hover:bg-gray-600 text-gray-200"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <span className="text-xs text-gray-500">
                                {(item.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
                {rootFolder.id !== 'general' && rootFolder.items.map((item) => {
                  if ('items' in item) {
                    return (
                      <div key={item.id} className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800 mb-3 hover:border-gray-500 transition-colors">
                        <div
                          className={`flex items-center p-3 cursor-pointer hover:bg-gray-700 transition-colors
                            ${selectedFolder?.id === item.id ? 'bg-gray-700 ring-2 ring-blue-500' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFolder(item.id);
                            handleFolderSelect(item.id, item.name, rootFolder.id);
                          }}
                        >
                          <div onClick={(e) => {
                            e.stopPropagation();
                            toggleFolder(item.id);
                          }}>
                            {item.isOpen ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                          </div>
                          <Folder className="ml-2 text-gray-400" size={16} />
                          <span className="ml-2 font-medium text-gray-300">{item.name}</span>
                          <span className="ml-2 text-sm text-gray-500">
                            ({item.items.length} {item.items.length === 1 ? 'ficheiro' : 'ficheiros'})
                          </span>
                        </div>

                        {item.isOpen && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4 bg-gray-800">
                            <UploadCard 
                              folderId={item.id} 
                              folderName={item.name} 
                              parentId={rootFolder.id}
                              onUploadComplete={loadMediaItems}
                            />
                            {item.items.slice(0, item.currentPage * ITEMS_PER_PAGE).map((subItem) => (
                              <motion.div
                                key={subItem.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`relative group bg-gray-800 rounded-lg overflow-hidden border border-gray-700 hover:border-gray-600
                                  ${selectedItems.includes(subItem.id) ? 'ring-2 ring-blue-500' : ''}`}
                                onClick={() => {
                                  setSelectedItems(prev =>
                                    prev.includes(subItem.id)
                                      ? prev.filter(i => i !== subItem.id)
                                      : [...prev, subItem.id]
                                  );
                                }}
                              >
                                <div className="aspect-square relative">
                                  {subItem.type === 'image' ? (
                                    <div className="w-full h-full">
                                      <img
                                        src={subItem.url}
                                        alt={subItem.name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.currentTarget.src = PLACEHOLDER_IMAGE;
                                        }}
                                      />
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDelete([subItem.id]);
                                        }}
                                        className="absolute top-2 right-2 p-1.5 bg-destructive/80 hover:bg-destructive text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                      <Music size={48} className="text-gray-500" />
                                    </div>
                                  )}
                                </div>
                                <div className="p-3">
                                  {editingFileId === subItem.id ? (
                                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="text"
                                        value={editingFileName}
                                        onChange={(e) => setEditingFileName(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleRenameFile(subItem, editingFileName);
                                          } else if (e.key === 'Escape') {
                                            e.preventDefault();
                                            setEditingFileId(null);
                                            setEditingFileName('');
                                          }
                                        }}
                                        className="w-full px-2 py-1 bg-gray-700 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none text-gray-200"
                                        autoFocus
                                      />
                                    </div>
                                  ) : (
                                    <p className="font-medium text-gray-200 truncate">{subItem.name}</p>
                                  )}
                                  <div className="flex justify-between items-center">
                                    <p className="text-sm text-gray-400">
                                      {new Date(subItem.createdAt).toLocaleDateString()}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingFileId(subItem.id);
                                          setEditingFileName(subItem.name);
                                        }}
                                        className="p-1 bg-gray-700 rounded-full shadow-lg hover:bg-gray-600 text-gray-200"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <span className="text-xs text-gray-500">
                                        {(subItem.size / 1024 / 1024).toFixed(2)} MB
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                            {item.hasMore && <LoadMoreTrigger folderId={rootFolder.id} subFolderId={item.id} />}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
                {rootFolder.hasMore && <LoadMoreTrigger folderId={rootFolder.id} />}
              </div>
            )}
          </div>
        ))}

        {filteredFolders.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">
              {searchQuery
                ? 'Nenhuma pasta ou ficheiro encontrado com os filtros atuais'
                : 'Nenhuma pasta com média disponível'}
            </p>
          </div>
        )}
      </div>

      <div
        {...getRootProps()}
        className={`mt-6 border-2 border- dashed rounded-lg p-8 text-center cursor-pointer transition-colors relative
          ${isDragActive ? 'border-blue-500 bg-gray-700' : 'border-gray-600 hover:border-blue-500'}
          ${isUploading ? 'opacity-50 pointer-events-none' : ''}
          ${!selectedFolder ? 'border-yellow-500' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-2">
          <Image size={48} className={`${!selectedFolder ? 'text-yellow-500' : 'text-gray-400'}`} />
          {isUploading ? (
            <p className="text-gray-300">A carregar ficheiros...</p>
          ) : (
            <>
              {selectedFolder ? (
                <>
                  <p className="text-gray-300">
                    Arraste ficheiros para aqui ou clique para selecionar
                  </p>
                  <p className="text-sm text-blue-400">
                    Upload para: {selectedFolder.parentId ? `${rootFolders.find(f => f.id === selectedFolder.parentId)?.name} / ` : ''}{selectedFolder.name}
                  </p>
                </>
              ) : (
                <p className="text-yellow-500 font-medium">
                  Selecione uma pasta antes de fazer upload
                </p>
              )}
              <p className="text-sm text-gray-400">
                Suporta imagens (PNG, JPG, GIF) e áudio (MP3, WAV)
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaManager;